/**
 * Shared hooks for v2 action table components.
 */

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ProposedAction } from '../../lib/proposals/core/types';
import type { SelectionState } from '../../lib/proposals/types/selection';
import { ACTION_TABLE_PAGE_SIZE } from '../../lib/proposals/utils/constants';

/**
 * Encapsulates the four selection refs, their effect sync, and the stable
 * handleToggle / handleHeaderCheckboxChange callbacks.
 *
 * Returns stable function references that are safe to pass as props or put in
 * dependency arrays without causing re-renders.
 */
export function useTableSelectionHandlers(
  selectionState: SelectionState | undefined
) {
  const toggleRef = useRef(selectionState?.toggleAction);
  useEffect(() => {
    toggleRef.current = selectionState?.toggleAction;
  }, [selectionState?.toggleAction]);

  const selectAllRef = useRef(selectionState?.selectAll);
  const deselectAllRef = useRef(selectionState?.deselectAll);
  const isAllSelectedRef = useRef(selectionState?.isAllSelected);
  useEffect(() => {
    selectAllRef.current = selectionState?.selectAll;
    deselectAllRef.current = selectionState?.deselectAll;
    isAllSelectedRef.current = selectionState?.isAllSelected;
  }, [
    selectionState?.selectAll,
    selectionState?.deselectAll,
    selectionState?.isAllSelected,
  ]);

  const handleToggle = useCallback((id: number) => {
    toggleRef.current?.(id);
  }, []);

  const handleHeaderCheckboxChange = useCallback(() => {
    if (isAllSelectedRef.current) {
      deselectAllRef.current?.();
    } else {
      selectAllRef.current?.();
    }
  }, []);

  return { handleToggle, handleHeaderCheckboxChange };
}

/**
 * Stable click and keyboard handlers for table data rows.
 *
 * Extracts the duplicated onClick/onKeyDown pattern shared across
 * ActionDataRow, MixedActionDataRow, and HomogeneousDataRow into a single
 * reusable hook.
 */
export function useRowInteraction(
  actionId: number,
  onRowClick: (id: number) => void
) {
  const handleClick = useCallback(
    () => onRowClick(actionId),
    [onRowClick, actionId]
  );
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick(actionId);
      }
    },
    [onRowClick, actionId]
  );
  return { handleClick, handleKeyDown };
}

/**
 * Encapsulates pagination state and row-expand state for action tables.
 *
 * @param actions  The (already-filtered) list of actions to paginate.
 * @param pageSize Number of rows per page (defaults to ACTION_TABLE_PAGE_SIZE).
 */
export function useTablePaginationState(
  actions: ProposedAction[],
  pageSize: number = ACTION_TABLE_PAGE_SIZE
) {
  const [currentPage, setCurrentPage] = useState(1);
  // These hold a VERSION-STABLE action key (see `getStableActionKey`), not a
  // row id. Saving mints a new row id, so a raw-id comparison would silently
  // stop matching and collapse the row the user is working in — note that a
  // stable React key alone does NOT prevent that, because the expanded row is
  // dropped from the list before keys are even considered.
  const [expandedActionKey, setExpandedActionKey] = useState<number | null>(
    null
  );
  // When a user clicks the per-row Edit button, we expand the row AND flag it
  // for auto-edit so the expanded row enters edit mode on mount. The flag
  // clears as soon as the user collapses the row or changes pages — chevron
  // clicks should always be a read-mode expand, never an edit-mode expand.
  const [pendingEditActionKey, setPendingEditActionKey] = useState<
    number | null
  >(null);

  const totalPages = Math.max(1, Math.ceil(actions.length / pageSize));
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedActions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return actions.slice(startIndex, startIndex + pageSize);
  }, [actions, currentPage, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setExpandedActionKey(null);
    setPendingEditActionKey(null);
  }, []);

  /** @param actionKey a `getStableActionKey` value, NOT a raw `action.id`. */
  const handleRowClick = useCallback((actionKey: number) => {
    setExpandedActionKey(prev => (prev === actionKey ? null : actionKey));
    // Chevron/row toggle is always a read-mode expand — clear any pending
    // edit so a subsequent re-expand doesn't silently re-trigger edit mode.
    setPendingEditActionKey(null);
  }, []);

  /** @param actionKey a `getStableActionKey` value, NOT a raw `action.id`. */
  const handleRowEditClick = useCallback((actionKey: number) => {
    setExpandedActionKey(actionKey);
    setPendingEditActionKey(actionKey);
  }, []);

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedActions,
    expandedActionKey,
    setExpandedActionKey,
    pendingEditActionKey,
    handlePageChange,
    handleRowClick,
    handleRowEditClick,
  };
}
